// Admin observability + evaluation console for KA Intelligence.
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Play, ShieldAlert } from "lucide-react";
import {
  getIntelligenceOverview,
  runIntelligenceEvalSuite,
} from "@/lib/intelligence.functions";
import { ROLE_LABELS } from "@/lib/intelligence/router";

export const Route = createFileRoute("/_authenticated/admin/intelligence")({
  head: () => ({
    meta: [
      { title: "KA Intelligence Console | KAfrequency Admin" },
      { name: "description", content: "Quality, cost, safety and evaluation console for KA Intelligence." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminIntelligence,
});

const CATEGORIES = ["identity", "canonical", "spiritual", "creative", "commercial", "safety", "refusal", "routing"];

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/50 p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function AdminIntelligence() {
  const fetchOverview = useServerFn(getIntelligenceOverview);
  const runSuite = useServerFn(runIntelligenceEvalSuite);
  const [suite, setSuite] = useState<"routing" | "live">("routing");
  const [category, setCategory] = useState<string>("");
  const [limit, setLimit] = useState(20);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["intelligence-overview"],
    queryFn: () => fetchOverview(),
  });

  const evalRun = useMutation({
    mutationFn: () => runSuite({ data: { suite, category: category || null, limit } }),
    onSuccess: () => void refetch(),
  });

  if (isLoading) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-10">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading console…
        </p>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-10">
        <p role="alert" className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          {(error as Error)?.message ?? "Unable to load the console."}
        </p>
      </main>
    );
  }

  const groundedPct = data.totals.runs ? Math.round((data.totals.grounded / data.totals.runs) * 100) : 0;
  const errorPct = data.totals.runs ? Math.round((data.totals.errors / data.totals.runs) * 100) : 0;
  const satisfaction = data.totals.rated ? Math.round((data.totals.positive / data.totals.rated) * 100) : 0;

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <header className="mb-6">
        <h1 className="font-display text-2xl">KA Intelligence Console</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Last 14 days. Telemetry records shape only — no prompts or answers are stored.
        </p>
      </header>

      <section aria-label="Provider health" className="mb-6 rounded-xl border border-border/60 bg-card/40 p-4">
        <h2 className="text-sm font-medium">Provider</h2>
        <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-4">
          <div><dt className="text-xs text-muted-foreground">Transport</dt><dd>{data.provider.transport}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Status</dt><dd>{data.provider.configured ? "configured" : "not configured"}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Primary model</dt><dd>{data.provider.primaryModel}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Fast model</dt><dd>{data.provider.fastModel}</dd></div>
        </dl>
      </section>

      <section aria-label="Key metrics" className="mb-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Runs" value={String(data.totals.runs)} />
        <Stat label="Grounded" value={`${groundedPct}%`} hint="answers backed by first-party sources" />
        <Stat label="Errors" value={`${errorPct}%`} />
        <Stat label="Latency p50 / p95" value={`${Math.round(data.latency.p50 / 100) / 10}s / ${Math.round(data.latency.p95 / 100) / 10}s`} />
        <Stat label="Satisfaction" value={data.totals.rated ? `${satisfaction}%` : "—"} hint={`${data.totals.rated} ratings`} />
      </section>

      <section aria-label="By specialist" className="mb-8">
        <h2 className="mb-2 text-sm font-medium">By specialist</h2>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="bg-card/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="p-2">Role</th><th className="p-2">Runs</th><th className="p-2">Errors</th><th className="p-2">Avg latency</th></tr>
            </thead>
            <tbody>
              {data.byRole.length === 0 ? (
                <tr><td className="p-3 text-muted-foreground" colSpan={4}>No runs yet.</td></tr>
              ) : (
                data.byRole.map((r) => (
                  <tr key={r.role} className="border-t border-border/40">
                    <td className="p-2">{ROLE_LABELS[r.role as keyof typeof ROLE_LABELS] ?? r.role}</td>
                    <td className="p-2">{r.runs}</td>
                    <td className="p-2">{r.errors}</td>
                    <td className="p-2">{Math.round(r.avgLatency / 100) / 10}s</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-label="Safety flags" className="mb-8">
        <h2 className="mb-2 text-sm font-medium">Safety-flagged turns</h2>
        {data.safety.length === 0 ? (
          <p className="text-sm text-muted-foreground">None in this window.</p>
        ) : (
          <ul className="flex flex-wrap gap-2 text-sm">
            {data.safety.map((s) => (
              <li key={s.flag} className="rounded-full border border-border px-3 py-1">
                {s.flag}: {s.runs}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Evaluation" className="mb-8 rounded-xl border border-border/60 bg-card/40 p-4">
        <h2 className="text-sm font-medium">Evaluation suite</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Routing runs offline and is free. Live runs real model calls and consumes credits — keep the
          case limit small.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Suite</span>
            <select value={suite} onChange={(e) => setSuite(e.target.value as "routing" | "live")} className="rounded-md border border-border bg-background px-2 py-1 text-sm">
              <option value="routing">Routing (offline)</option>
              <option value="live">Live (model calls)</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm">
              <option value="">All</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Max cases</span>
            <input type="number" min={1} max={60} value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm" />
          </label>
          <button
            type="button"
            onClick={() => evalRun.mutate()}
            disabled={evalRun.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-gold px-3 py-1.5 text-sm text-background disabled:opacity-50"
          >
            {evalRun.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            Run suite
          </button>
        </div>

        {evalRun.isError ? (
          <p role="alert" className="mt-3 text-sm text-destructive">{(evalRun.error as Error).message}</p>
        ) : null}

        {evalRun.data ? (
          <div className="mt-4">
            <p className="text-sm">
              {evalRun.data.suite}: <strong>{evalRun.data.passed}</strong> passed, {evalRun.data.failed} failed of {evalRun.data.total}.
            </p>
            <ul className="mt-2 space-y-1 text-xs">
              {evalRun.data.results.filter((r) => !r.passed).map((r) => (
                <li key={r.id} className="rounded border border-destructive/30 bg-destructive/5 p-2">
                  <strong>{r.id}</strong> ({r.category}, routed {r.role}) — {r.failures.join("; ")}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section aria-label="Recent runs">
        <h2 className="mb-2 text-sm font-medium">Recent runs</h2>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="bg-card/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-2">When</th><th className="p-2">Role</th><th className="p-2">Status</th>
                <th className="p-2">Grounded</th><th className="p-2">Tools</th><th className="p-2">Latency</th><th className="p-2">Rating</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.length === 0 ? (
                <tr><td className="p-3 text-muted-foreground" colSpan={7}>No runs yet.</td></tr>
              ) : (
                data.recent.map((r) => (
                  <tr key={r.id} className="border-t border-border/40">
                    <td className="p-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="p-2">{ROLE_LABELS[r.role as keyof typeof ROLE_LABELS] ?? r.role}</td>
                    <td className="p-2">{r.status}{r.safety_flag ? ` · ${r.safety_flag}` : ""}</td>
                    <td className="p-2">{r.grounded ? "yes" : "no"}</td>
                    <td className="p-2">{r.tool_calls}</td>
                    <td className="p-2">{r.latency_ms ? `${Math.round(r.latency_ms / 100) / 10}s` : "—"}</td>
                    <td className="p-2">{r.rating === 1 ? "up" : r.rating === -1 ? "down" : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
